"use client";

import dynamic from "next/dynamic";
import { createElement } from "react";
import type { AnalyticsDemoAnimationProps } from "./AnalyticsDemoAnimation";
import type { AllInOneEditorDemoProps } from "./AllInOneEditorDemo";
import type { AudiobookReaderDemoProps } from "./AudiobookReaderDemo";
import type { AudiobooksUIDemoProps } from "./AudiobooksUIDemo";
import type { AgentOrbDemoProps } from "./AgentOrbDemo";
import type { MultiModalAgentOrbProps } from "@ui/components/demos/MultiModalAgentOrb/MultiModalAgentOrb";
import type { BeforeAfterDemoProps } from "./BeforeAfterDemo";
import type { ChartsDemoProps } from "./ChartsDemo";
import type { ChatScenarioDemoProps } from "./ChatScenarioDemo";
import type { GuardrailsDemoProps } from "./GuardrailsDemo";
import type { EmailsDemoProps } from "./EmailsDemo";
import type { CreativeTemplatesModelsDemoClientProps } from "./CreativeTemplatesModelsDemoClient";
import type { DiscPlayerDemoProps } from "./DiscPlayerDemo";
import type { LanguagesGlobeDemoProps } from "./LanguagesGlobeDemo";
import type { VideoComparisonDemoProps } from "./VideoComparisonDemo";
import type { VideoScrollerDemoProps } from "./VideoScrollerDemo";
import type { VoiceCompareDemoProps } from "./VoiceCompareDemo/VoiceCompareDemo";
import type { AudioTranscriptDemoProps } from "../BlockContentGrid/AudioTranscriptDemo";
import type { VoiceListDemoProps } from "./VoiceListDemo";

type EmptyProps = {};

type LazyComponentPropsMap = {
  multimodalAgentsDemo: EmptyProps;
  allInOneEditorDemo: AllInOneEditorDemoProps;
  audiobooksUIDemo: AudiobooksUIDemoProps;
  analyticsDemoAnimation: AnalyticsDemoAnimationProps;
  chartsDemo: ChartsDemoProps;
  languagesGlobeDemo: LanguagesGlobeDemoProps;
  scribeV1toV2Demo: EmptyProps;
  callTranscriptionDemo: EmptyProps;
  wordPredictionDemo: EmptyProps;
  guardrailsDemo: GuardrailsDemoProps;
  beforeAfterDemo: BeforeAfterDemoProps;
  lipSyncDemo: EmptyProps;
  creativeTemplatesModelsDemo: CreativeTemplatesModelsDemoClientProps;
  lipSyncAvatarDemo: EmptyProps;
  videoScrollerDemo: VideoScrollerDemoProps;
  ttsControlsDemo: EmptyProps;
  voiceCloneDemo: EmptyProps;
  discPlayerDemo: DiscPlayerDemoProps;
  voiceCompareDemo: VoiceCompareDemoProps;
  musicUIDemo: EmptyProps;
  simpleMusicDemo: EmptyProps;
  videoComparisonDemo: VideoComparisonDemoProps;
  audioTranscriptDemo: AudioTranscriptDemoProps;
  audiobookReaderDemo: AudiobookReaderDemoProps;
  creatorProfileDemo: EmptyProps;
  musicFinetuningDemo: EmptyProps;
  musicFinetuneUIDemo: EmptyProps;
  musicV2UIDemo: EmptyProps;
  musicV2InpaintingDemo: EmptyProps;
  musicV2MarketplaceDemo: EmptyProps;
  dubbingV2Demo: EmptyProps;
  elevenMusicDemo: EmptyProps;
  agentOrbDemo: AgentOrbDemoProps;
  multiModalAgentOrbDemo: MultiModalAgentOrbProps;
  chatScenarioDemo: ChatScenarioDemoProps;
  speechEngineDemo: EmptyProps;
  emailsDemo: EmailsDemoProps;
  voiceListDemo: VoiceListDemoProps;
};

type LazyComponentName = keyof LazyComponentPropsMap;

const loadComponent = <Props,>(loader: () => Promise<React.ComponentType<Props>>) => dynamic<Props>(loader);

const MultimodalAgentsDemo = loadComponent<LazyComponentPropsMap["multimodalAgentsDemo"]>(() =>
  import("./MultimodalAgentsDemo").then(mod => mod.MultimodalAgentsDemo),
);
const AllInOneEditorDemo = loadComponent<LazyComponentPropsMap["allInOneEditorDemo"]>(() =>
  import("./AllInOneEditorDemo").then(mod => mod.AllInOneEditorDemo),
);
const AudiobooksUIDemo = loadComponent<LazyComponentPropsMap["audiobooksUIDemo"]>(() =>
  import("./AudiobooksUIDemo").then(mod => mod.AudiobooksUIDemo),
);
const AnalyticsDemoAnimation = loadComponent<LazyComponentPropsMap["analyticsDemoAnimation"]>(() =>
  import("./AnalyticsDemoAnimation").then(mod => mod.AnalyticsDemoAnimation),
);
const ChartsDemo = loadComponent<LazyComponentPropsMap["chartsDemo"]>(() =>
  import("./ChartsDemo").then(mod => mod.ChartsDemo),
);
const LanguagesGlobeDemo = loadComponent<LazyComponentPropsMap["languagesGlobeDemo"]>(() =>
  import("./LanguagesGlobeDemo").then(mod => mod.LanguagesGlobeDemo),
);
const ScribeV1toV2Demo = loadComponent<LazyComponentPropsMap["scribeV1toV2Demo"]>(() =>
  import("./ScribeV1toV2Demo").then(mod => mod.ScribeV1toV2Demo),
);
const CallTranscriptionDemo = loadComponent<LazyComponentPropsMap["callTranscriptionDemo"]>(() =>
  import("./CallTranscriptionDemo").then(mod => mod.CallTranscriptionDemo),
);
const WordPredictionDemo = loadComponent<LazyComponentPropsMap["wordPredictionDemo"]>(() =>
  import("./WordPredictionDemo").then(mod => mod.WordPredictionDemo),
);
const GuardrailsDemo = loadComponent<LazyComponentPropsMap["guardrailsDemo"]>(() =>
  import("./GuardrailsDemo").then(mod => mod.GuardrailsDemo),
);
const BeforeAfterDemo = loadComponent<LazyComponentPropsMap["beforeAfterDemo"]>(() =>
  import("./BeforeAfterDemo").then(mod => mod.BeforeAfterDemo),
);
const LipSyncDemo = loadComponent<LazyComponentPropsMap["lipSyncDemo"]>(() =>
  import("./LipSyncDemo").then(mod => mod.LipSyncDemo),
);
const CreativeTemplatesModelsDemoClient = loadComponent<LazyComponentPropsMap["creativeTemplatesModelsDemo"]>(() =>
  import("./CreativeTemplatesModelsDemoClient").then(mod => mod.CreativeTemplatesModelsDemoClient),
);
const LipSyncAvatarDemo = loadComponent<LazyComponentPropsMap["lipSyncAvatarDemo"]>(() =>
  import("./LipSyncAvatarDemo").then(mod => mod.LipSyncAvatarDemo),
);
const VideoScrollerDemo = loadComponent<LazyComponentPropsMap["videoScrollerDemo"]>(() =>
  import("./VideoScrollerDemo").then(mod => mod.VideoScrollerDemo),
);
const TTSControlsDemo = loadComponent<LazyComponentPropsMap["ttsControlsDemo"]>(() =>
  import("./TTSControlsDemo").then(mod => mod.TTSControlsDemo),
);
const VoiceCloneDemo = loadComponent<LazyComponentPropsMap["voiceCloneDemo"]>(() =>
  import("./VoiceCloneDemo").then(mod => mod.VoiceCloneDemo),
);
const DiscPlayerDemo = loadComponent<LazyComponentPropsMap["discPlayerDemo"]>(() =>
  import("./DiscPlayerDemo").then(mod => mod.DiscPlayerDemo),
);
const VoiceCompareDemo = loadComponent<LazyComponentPropsMap["voiceCompareDemo"]>(() =>
  import("./VoiceCompareDemo/VoiceCompareDemo").then(mod => mod.VoiceCompareDemo),
);
const MusicUIDemo = loadComponent<LazyComponentPropsMap["musicUIDemo"]>(() =>
  import("./MusicUIDemo").then(mod => mod.MusicUIDemo),
);
const SimpleMusicDemo = loadComponent<LazyComponentPropsMap["simpleMusicDemo"]>(() =>
  import("./SimpleMusicDemo").then(mod => mod.SimpleMusicDemo),
);
const VideoComparisonDemo = loadComponent<LazyComponentPropsMap["videoComparisonDemo"]>(() =>
  import("./VideoComparisonDemo").then(mod => mod.VideoComparisonDemo),
);
const AudioTranscriptDemo = loadComponent<LazyComponentPropsMap["audioTranscriptDemo"]>(() =>
  import("../BlockContentGrid/AudioTranscriptDemo").then(mod => mod.AudioTranscriptDemo),
);
const AudiobookReaderDemo = loadComponent<LazyComponentPropsMap["audiobookReaderDemo"]>(() =>
  import("./AudiobookReaderDemo").then(mod => mod.AudiobookReaderDemo),
);
const CreatorProfileDemo = loadComponent<LazyComponentPropsMap["creatorProfileDemo"]>(() =>
  import("./CreatorProfileDemo").then(mod => mod.CreatorProfileDemo),
);
const MusicFinetuningDemo = loadComponent<LazyComponentPropsMap["musicFinetuningDemo"]>(() =>
  import("./MusicFinetuningDemo").then(mod => mod.MusicFinetuningDemo),
);
const MusicFinetuneUIDemo = loadComponent<LazyComponentPropsMap["musicFinetuneUIDemo"]>(() =>
  import("./MusicFinetuneUIDemo").then(mod => mod.MusicFinetuneUIDemo),
);
const MusicV2UIDemo = loadComponent<LazyComponentPropsMap["musicV2UIDemo"]>(() =>
  import("./MusicV2UIDemo").then(mod => mod.MusicV2UIDemo),
);
const MusicV2InpaintingDemo = loadComponent<LazyComponentPropsMap["musicV2InpaintingDemo"]>(() =>
  import("./MusicV2InpaintingDemo").then(mod => mod.MusicV2InpaintingDemo),
);
const MusicV2MarketplaceDemo = loadComponent<LazyComponentPropsMap["musicV2MarketplaceDemo"]>(() =>
  import("./MusicV2MarketplaceDemo").then(mod => mod.MusicV2MarketplaceDemo),
);
const DubbingV2Demo = loadComponent<LazyComponentPropsMap["dubbingV2Demo"]>(() =>
  import("./DubbingV2Demo").then(mod => mod.DubbingV2Demo),
);
const ElevenMusicDemo = loadComponent<LazyComponentPropsMap["elevenMusicDemo"]>(() =>
  import("./ElevenMusicDemo").then(mod => mod.ElevenMusicDemo),
);
const AgentOrbDemo = loadComponent<LazyComponentPropsMap["agentOrbDemo"]>(() =>
  import("./AgentOrbDemo").then(mod => mod.AgentOrbDemo),
);
const MultiModalAgentOrbDemo = loadComponent<LazyComponentPropsMap["multiModalAgentOrbDemo"]>(() =>
  import("@ui/components/demos/MultiModalAgentOrb/MultiModalAgentOrb").then(mod => mod.MultiModalAgentOrb),
);
const ChatScenarioDemo = loadComponent<LazyComponentPropsMap["chatScenarioDemo"]>(() =>
  import("./ChatScenarioDemo").then(mod => mod.ChatScenarioDemo),
);
const SpeechEngineDemo = loadComponent<LazyComponentPropsMap["speechEngineDemo"]>(() =>
  import("./SpeechEngineDemo").then(mod => mod.SpeechEngineDemo),
);
const EmailsDemo = loadComponent<LazyComponentPropsMap["emailsDemo"]>(() =>
  import("./EmailsDemo").then(mod => mod.EmailsDemo),
);
const VoiceListDemo = loadComponent<LazyComponentPropsMap["voiceListDemo"]>(() =>
  import("./VoiceListDemo").then(mod => mod.VoiceListDemo),
);

const components: { [Name in LazyComponentName]: React.ComponentType<LazyComponentPropsMap[Name]> } = {
  multimodalAgentsDemo: MultimodalAgentsDemo,
  allInOneEditorDemo: AllInOneEditorDemo,
  audiobooksUIDemo: AudiobooksUIDemo,
  analyticsDemoAnimation: AnalyticsDemoAnimation,
  chartsDemo: ChartsDemo,
  languagesGlobeDemo: LanguagesGlobeDemo,
  scribeV1toV2Demo: ScribeV1toV2Demo,
  callTranscriptionDemo: CallTranscriptionDemo,
  wordPredictionDemo: WordPredictionDemo,
  guardrailsDemo: GuardrailsDemo,
  beforeAfterDemo: BeforeAfterDemo,
  lipSyncDemo: LipSyncDemo,
  creativeTemplatesModelsDemo: CreativeTemplatesModelsDemoClient,
  lipSyncAvatarDemo: LipSyncAvatarDemo,
  videoScrollerDemo: VideoScrollerDemo,
  ttsControlsDemo: TTSControlsDemo,
  voiceCloneDemo: VoiceCloneDemo,
  discPlayerDemo: DiscPlayerDemo,
  voiceCompareDemo: VoiceCompareDemo,
  musicUIDemo: MusicUIDemo,
  simpleMusicDemo: SimpleMusicDemo,
  videoComparisonDemo: VideoComparisonDemo,
  audioTranscriptDemo: AudioTranscriptDemo,
  audiobookReaderDemo: AudiobookReaderDemo,
  creatorProfileDemo: CreatorProfileDemo,
  musicFinetuningDemo: MusicFinetuningDemo,
  musicFinetuneUIDemo: MusicFinetuneUIDemo,
  musicV2UIDemo: MusicV2UIDemo,
  musicV2InpaintingDemo: MusicV2InpaintingDemo,
  musicV2MarketplaceDemo: MusicV2MarketplaceDemo,
  dubbingV2Demo: DubbingV2Demo,
  elevenMusicDemo: ElevenMusicDemo,
  chatScenarioDemo: ChatScenarioDemo,
  agentOrbDemo: AgentOrbDemo,
  multiModalAgentOrbDemo: MultiModalAgentOrbDemo,
  speechEngineDemo: SpeechEngineDemo,
  emailsDemo: EmailsDemo,
  voiceListDemo: VoiceListDemo,
};

type LazyComponentProps<Name extends LazyComponentName> = {
  name: Name;
} & LazyComponentPropsMap[Name];

type ResolvedLazyComponentProps<Name extends LazyComponentName> = NonNullable<LazyComponentPropsMap[Name]>;

function renderLazyComponent<Name extends LazyComponentName>(name: Name, props: ResolvedLazyComponentProps<Name>) {
  const Component = components[name] as React.ComponentType<ResolvedLazyComponentProps<Name>>;

  return createElement(Component, props);
}

export function LazyComponent<Name extends LazyComponentName>({ name, ...props }: LazyComponentProps<Name>) {
  return renderLazyComponent(name, props as unknown as ResolvedLazyComponentProps<Name>);
}
